<template>
  <div class="details-component">
    <div class="details-component__header">
      <h1 v-if="isNew">
        {{ $t('parking.newParking') }}
      </h1>
      <h1 v-else>
        {{ $t('parking.editParking') }}
      </h1>
    </div>
    <div class="details-component__content content details-component-with-calendar">
      <form class="form fixed-form">
        <table class="details-table tenant no-col-padding edit-table fixed-table label-col-wide-150">
          <tr v-if="isSuperAdmin()" class="details-table__row"
            :class="{ 'details-table__row_error': errors.has('landlord') }">
            <td class="details-table__col details-table__col_label">
              <label for="landlord" class="form__label">{{ $t('common.landlord') }}</label>
            </td>
            <td class="details-table__col landlord__name">
              <el-select v-if="isNew && landlords.items" v-model="landlord" v-validate="'validLandlord'" name="landlord"
                :data-vv-as="$t('common.landlord')" :placeholder="$t('common.landlord')" :disabled="!isNew"
                @change="selectedTenant = null, canEditGuest, fetchTenants()">
                <el-option v-for="landlord in landlords.items" :key="landlord.id" :label="landlord.name"
                  :value="landlord.id" />
              </el-select>
              <span v-else-if="form.partner && form.partner.landlord.name" class="disabled">{{
                form.partner.landlord.name }}</span>
              <tooltip v-show="errors.has('landlord')" class="form__validation">
                {{ $t('errors.landlord_field_required') }}
              </tooltip>
            </td>
          </tr>

          <tr v-if="isLandlord()" class="details-table__row"
            :class="{ 'details-table__row_error': errors.has('selectedTenant') }">
            <td class="details-table__col details-table__col_label">
              <label for="tenant" class="form__label">{{ $t('common.tenant') }}</label>
            </td>
            <td class="details-table__col">
              <el-select v-if="tenants.data" v-model="selectedTenant" v-validate="'required'" name="selectedTenant"
                :data-vv-as="$t('common.tenant')" :disabled="!isNew" placeholder="" @change="canEditGuest">
                <el-option v-for="tenant in tenants.data" :key="tenant.id" :label="tenant.name" :value="tenant.id" />
              </el-select>
              <tooltip v-show="errors.has('selectedTenant')" class="form__validation">
                {{ $t('errors.tenant_field_required') }}
              </tooltip>
            </td>
          </tr>

          <tr v-if="isTenant() || selectedTenant" class="details-table__row"
            :class="{ 'details-table__row_error': errors.has('product') }">
            <td class="details-table__col details-table__col_label">
              <label for="product" class="form__label">{{ $t('common.product') }}</label>
            </td>
            <td class="details-table__col">
              <el-select ref="sortableTable" v-model="productId" v-validate="'required'" name="product"
                :data-vv-as="$t('common.product')" :disabled="!canEditPack() || !isNew" placeholder=""
                @change="canEditGuest">
                <SortableOption v-for="(product, index) in products.data" :key="product.product.id" ref="sort"
                  :index="index" :size="products.data.length" :label="product.product.name" :value="product.product.id"
                  @reorder="saveOrder($event)" />
              </el-select>
              <tooltip v-show="errors.has('product')" class="form__validation">
                {{ $t('errors.product_field_required') }}
              </tooltip>
            </td>
          </tr>

          <tr v-if="product && product.bookable_parking_spots_count > 0" class="details-table__row"
            :class="{ 'details-table__row_error': errors.has('bookableSpot') }">
            <td class="details-table__col details-table__col_label">
              <label for="product" class="form__label">{{ $t('common.bookable_spot') }}</label>
            </td>
            <td class="details-table__col">
              <el-select ref="sortableTable" v-model="form.spot" v-validate="'required'" name="bookableSpot"
                :data-vv-as="$t('common.product')" placeholder="">
                <SortableOption v-for="(spot, index) in product.bookable_parking_spots_list" :key="spot.id" ref="sort"
                  :index="index" :size="product.bookable_parking_spots_list.length"
                  :label="`${$t('common.spot')} ${index + 1} ${formatBookableSpotDate(spot)}`" :value="spot.id" />
              </el-select>
              <tooltip v-show="errors.has('bookableSpot')" class="form__validation">
                {{ $t('errors.bookable_parking_spots_count_required') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row"
            :class="{ 'details-table__row_error': vehicleValid || errors.has('vehicleReg') }">
            <td class="details-table__col details-table__col_label">
              <label for="vehicleReg" class="form__label">{{ $t('parking.vehicleReg') }}</label>
            </td>
            <td v-if="!form.vehicleReg && isNew" class="details-table__col vehicle-reg-input">
              <el-tag v-for="tag in vehicleRegList" :key="tag" closable :disable-transitions="false"
                @close="handleVehicleRegRemove(tag)">
                {{ tag }}
              </el-tag>
              <el-input ref="vehicleRegInputRef" v-model="vehicleReg" :disabled="!canGuestEdit && !isNew"
                @keydown="stopInputAtDashAndSpace" @keydown.enter.native.prevent.stop
                @keyup.enter.native="handleVehicleRegAdd" @blur="handleVehicleRegAdd" @focus="canEditGuest"
                @input="inputVehicleReg" />
              <tooltip v-show="vehicleValid" class="form__validation">
                {{ $t('errors.car_field_required') }}
              </tooltip>
            </td>
            <td v-else-if="form.vehicleReg" class="details-table__col">
              <input id="vehicleReg" v-model="form.vehicleReg" v-validate="'required'" type="text" class="form__input"
                name="vehicleReg"
                :disabled="parking.created_by.type !== 'App\\Models\\EasyParking' || !canEdit() && !canGuestEdit && !isNew"
                @keydown="stopInputAtDashAndSpace" @keyup="trimVehicleReg()" @focus="canEditGuest">
              <tooltip v-show="errors.has('vehicleReg')" class="form__validation">
                {{ $t('errors.car_field_required') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('comment') }">
            <td class="details-table__col details-table__col_label">
              <label for="comment" class="form__label">{{ $t('parking.comment') }}</label>
            </td>
            <td class="details-table__col">
              <input id="comment" v-model="form.comment" v-validate="{ required: isRequiredComment() }" type="text"
                class="form__input" name="comment" :disabled="!canGuestEdit && !isNew">
              <tooltip v-show="errors.has('comment')" class="form__validation">
                {{ $t('errors.field_required') }}
              </tooltip>
            </td>
          </tr>

          <tr class="details-table__row" :class="{ 'details-table__row_error': errors.has('mobile') }">
            <td class="details-table__col details-table__col_label">
              <label for="mobile" class="form__label">{{ $t('parking.mobile') }}</label>
            </td>
            <td class="details-table__col">
              <input id="mobile" v-model="form.mobile" type="text" class="form__input" name="mobile"
                :disabled="!canGuestEdit && !isNew">
              <tooltip v-show="errors.has('mobile')" class="form__validation">
                {{ $t('errors.field_required') }}
              </tooltip>
            </td>
          </tr>


          <tr v-if="hasNotStarted()" class="details-table__row form__field_datepicker"
            :class="{ 'details-table__row_error': errors.has('startTime') }">
            <td class="details-table__col details-table__col_label">
              <label class="form__label">{{ $t('parking.startTime') }}</label>
            </td>
            <td class="details-table__col">
              <el-date-picker v-model="form.startTime"
                v-validate="{ rules: { isValidStartDate: form.startTime !== null } }" format="dd.MM.yyyy HH:mm"
                type="datetime" :disabled="!canGuestEdit && !isNew" name="startTime" :default-time="defaultTime"
                :picker-options="startTimeOptions" @blur="formChanges"
                @change="checkedDatesBetweenEachOther" />
              <tooltip v-show="errors.has('startTime')" class="form__validation">
                {{ $t('errors.date') }}
              </tooltip>
            </td>
          </tr>

          <tr v-if="allowEndTimeOverride || (parking.product && parking.product.allow_end_time_override)"
            class="details-table__row form__field_datepicker"
            :class="{ 'details-table__row_error': errors.has('endTime') }">
            <td class="details-table__col details-table__col_label">
              <label class="form__label">{{ $t('parking.endTime') }}</label>
            </td>
            <td class="details-table__col auto-height">
              <el-date-picker v-model="form.endTime" v-validate="{ rules: { isValidEndDate: form.endTime !== null } }"
                format="dd.MM.yyyy HH:mm" type="datetime" name="endTime"
                :disabled="(!canEditEndTime && !isNew) || (!canGuestEdit && !canEditEndTime && !isNew)"
                :default-time="defaultTime" :picker-options="endTimeOptions" @blur="formChanges"
                @change="checkedDatesBetweenEachOther" />
              <tooltip v-show="errors.has('endTime')" class="form__validation">
                {{ $t('errors.date') }}
              </tooltip>
            </td>
          </tr>

          <tr v-if="expectedPrice > 0" class="details-table__row">
            <td class="details-table__col details-table__col_label">
              <label class="form__label">{{ $t('parking.price') }}</label>
            </td>
            <td class="details-table__col disabled">
              {{ expectedPrice }} {{ currency_short }}
            </td>
          </tr>

          <!-- <tr class="details-table__row auto-height" v-if="!isNew">
            <td>
              <label for="is_time_limit_edite" class="form__label">{{ $t('parking.time_limit_edite') }}</label>
            </td>
            <td class="details-table__col txt-to-left margin-t-30" colspan="3">
              <label class="form__label form__label__long no-hide with_checkbox">
                <input
                  type="checkbox"
                  id="can_exceed_capacity"
                  disabled="disabled"
                  name="is_time_limit_edite"
                  class="form__fieldset__checkbox"
                  v-model="form.product.is_time_limit_edit"
                >
                <span class="form__fieldset__custom-checkbox"></span>
              </label>
            </td>
          </tr> -->
        </table>

        <div class="form__buttons buttonset">
          <router-link v-if="isNew" :to="{ name: 'parkings' }" class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <router-link v-else :to="{ name: 'parking', params: { id: $route.params.id } }"
            class="buttonset__button btn btn--secondary">
            {{ $t('common.discard') }}
          </router-link>
          <button type="submit" class="buttonset__button btn btn--main" :disabled="submitted"
            @click.prevent.stop="onSubmit()">
            {{ $t('common.save') }}
          </button>
        </div>
      </form>
      <div v-if="occupancy" class="calendar-container">
        <header class="calendar-header">
          <p class="calendar-title">
            Occupancy calendar
          </p>
          <div class="calendar-nav-container">
            <p class="calendar-current-date">
              {{ months[month] }} {{ year }}
            </p>
            <div class="calendar-navigation">
              <span id="calendar-prev" class="material-symbols-rounded" @click="updateCalendarDate('decrement')">
                &larr;
              </span>
              <span id="calendar-next" class="material-symbols-rounded" @click="updateCalendarDate()">&rarr;</span>
            </div>
          </div>
        </header>
        <div class="calendar-body">
          <ul class="calendar-weekdays">
            <li>Sun</li>
            <li>Mon</li>
            <li>Tue</li>
            <li>Wed</li>
            <li>Thu</li>
            <li>Fri</li>
            <li>Sat</li>
          </ul>
          <ul class="calendar-dates" v-html="createDaysComponents()" />
        </div>
      </div>
    </div>
    <ConfirmationModal v-if="showMoreThanTwoWeeksModal" :confirm-text="$t('common.yes')"
      :unconfirm-text="$t('common.no')" @cancel="closeDatePicker" @confirmed="confirmedDatePicker">
      <h1 slot="header">
        {{ $t('errors.parking_period_more_than_two_weeks', { daysOfParking: daysBetweenDates }) }}
      </h1>
    </ConfirmationModal>
    <ConfirmationModal v-if="showConfirmationModal" :confirm-text="$t('common.add_anyway')"
      @cancel="showConfirmationModal = false"
      @confirmed="() => { showConfirmationModal = false; form.ignoreVehiclesRegisteredError = true; onSubmit(); }">
      <h1 slot="header">
        {{ $t('common.warning') }}
      </h1>
      <p slot="body">
        {{ confirmationModalBody }}
      </p>
    </ConfirmationModal>
    <WarningModal v-if="showWarningModal" @close="closeWarningModal">
      <h1 slot="header">
        {{ warningHeader }}
      </h1>
      <p slot="body">
        {{ warningBody }}
      </p>
    </WarningModal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import * as mTypes from 'store/mutation-types';
import Tooltip from 'common/Tooltip';
import ConfirmationModal from 'common/ConfirmationModal';
import WarningModal from 'common/WarningModal';
import SortableOption from 'components/sortable/SortableOption';
import 'styles/blocks/details.scss';
import { sanitizeVehicleRegNumber } from '../../utils';

export default {
  components: {
    Tooltip,
    ConfirmationModal,
    WarningModal,
    SortableOption,
  },
  data() {
    return {
      form: {
        startTime: null,
        endTime: null,
        spot: null,
      },
      months: [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'],
      date: new Date(),
      product: null,
      vehicleReg: '',
      vehicleRegList: [],
      landlord: null,
      productId: null,
      ruleHasSteps: false,
      showConfirmationModal: false,
      showWarningModal: false,
      confirmationModalBody: null,
      warningBody: null,
      warningHeader: null,
      selectedTenant: null,
      expectedPrice: null,
      lastPriceRequest: { startTime: null, endTime: null, productId: null },
      currency_short: process.env.CURRENCY_SHORT,
      canGuestEdit: true,
      isFinishParking: false,
      maximumEditingMinutes: 15,
      vehicleValid: false,
      canEditEndTime: true,
      startTimeOptions: {
        disabledDate: (date) => {
          const allowPast = this.product && this.product.allow_past_start;
          const isInPast = !allowPast && moment(date).isBefore(moment(), 'day');
          if (this.form.endTime) {
            return moment(this.form.endTime).isBefore(moment(date), 'day') || isInPast;
          }
          return isInPast;
        },
        firstDayOfWeek: 1,
      },
      endTimeOptions: {
        disabledDate: (date) => {
          const isInPast = moment(date).isBefore(moment(), 'day');
          if (this.form.startTime) {
            return moment(this.form.startTime).isAfter(moment(date), 'day') || isInPast;
          }
          return isInPast;
        },
        firstDayOfWeek: 1,
      },
      showMoreThanTwoWeeksModal: false,
      daysBetweenDates: '',
      timeBetweenDates: '',
      checkingSecondsBetweenDates: 1.21e9,
      secondsBetweenDates: null,
      submitted: false,
      notEnded: null,
      originalStartTime: null,
    };
  },
  computed: {
    isNew() {
      return !this.$route.params.id;
    },
    ...mapGetters({
      landlords: 'landlord/landlords/landlords',
      user: 'user/user/user',
      products: 'landlord/products/products',
      parkingsLimit: 'landlord/parkings/limit',
      parkingsKeywordFilter: 'landlord/parkings/keywordFilter',
      parking: 'parkings/parking/parking',
      parkingError: 'parkings/parking/error',
      pricingrules: 'pricingrules/pricingrules/pricingrules',
      tenants: 'landlord/tenants/tenants',
      allowEndTimeOverride: 'landlord/products/allowEndTimeOverride',
      occupancy: 'parkings/parking/occupancy',
    }),
    month() {
      return this.date.getMonth();
    },
    formattedMonth(){
      const monthIndex = this.month + 1;
      return String(monthIndex).padStart(2, '0');
    },
    year() {
      return this.date.getFullYear();
    },
    defaultTime() {
      return moment().set('second', 0).format('HH:mm:ss');
    },
    computedCalendarData() {
      const dayone = new Date(this.year, this.month, 1).getDay();
      const lastdate = new Date(this.year, this.month + 1, 0).getDate();
      const dayend = new Date(this.year, this.month, lastdate).getDay();
      const monthlastdate = new Date(this.year, this.month, 0).getDate();

      return { dayone, dayend, lastdate, monthlastdate };
    },
    occupancyDuringSelectedMonth() {
      const days = Object.entries(this.occupancy).filter(day => day[0].includes(`${this.year}-${this.formattedMonth}`));
      return Object.fromEntries(days);
    },
  },
  watch: {
    products: 'updateForm',
    productId: ['getPricingRulesForProduct', 'formChanges', 'getProduct'],
    selectedTenant: 'fetchTenantProducts',
    $route: 'checkView',
    product: 'fetchOccupancy',
  },
  mounted() {
    this.checkView();
    this.fetchAllData();
    // this.checkTimeLimitEdit();
  },
  destroyed() {
    this.$store.commit(`landlord/products/${mTypes.CLEAR_STATE}`);
  },
  methods: {
    formatBookableSpotDate(spot) {
      const { start_time, end_time } = spot;

      return `${start_time ? `(${moment(start_time, 'YYYY-MM-DD hh:mm:ss').format('MM-DD HH:mm')}` : ''} ${end_time ? `- ${moment(end_time, 'YYYY-MM-DD hh:mm:ss').format('MM-DD HH:mm')})` : ''}`;
    },
    updateCalendarDate(method) {
      const updatedMonth = method === 'decrement' ? this.month - 1 : this.month + 1;

      if (updatedMonth < 0 || updatedMonth > 11) {
        this.date = new Date(this.year, updatedMonth, new Date().getDate());
      } else {
        this.date = new Date(this.year, updatedMonth, new Date().getDate());
      }

      this.fetchOccupancy(updatedMonth);
    },
    formatMonths(month) {
      if (Number(month) < 10) {
        return `0${month}`;
      }
      return String(month);
    },
    formatDays(day) {
      if (Number(day) < 10) {
        return `0${day}`;
      }
      return String(day);
    },
    createDaysComponents() {
      const { dayone, dayend, lastdate, monthlastdate } = this.computedCalendarData
      let lit = '';

      for (let i = dayone; i > 0; i--) {
        lit += `<li class="inactive">${monthlastdate - i + 1}</li>`;
      }

      for (let i = 1; i <= lastdate; i++) {
        const formattedDay = this.formatDays(i);
        const isToday = i === this.date.getDate() && this.month === new Date().getMonth() && this.year === new Date().getFullYear() ? 'active' : '';
        const currentDayOccupancy = this.occupancyDuringSelectedMonth[`${this.year}-${this.formattedMonth}-${formattedDay}`];

        lit += `<li class="${isToday}"><div class="calendar-day"><span>${i}</span><span class="occupancy-value">${currentDayOccupancy || 0}</span></div></li>`;
      }

      for (let i = dayend; i < 6; i++) {
        lit += `<li class="inactive">${i - dayend + 1}</li>`;
      }

      return lit;
    },
    getProduct() {
      if (this.products.data && this.products.data.length > 0) {
        this.products.data.forEach((item) => {
          if (item.product.id === this.productId) {
            this.product = item.product;
            this.$store.dispatch(
              'landlord/products/setAllowEndTimeOverride',
              item.product.allow_end_time_override
            );
          }
        });
      }
    },
    timeToText(time) {
      let days = 0;
      let hours = 0;
      let minutes = 0;
      if (time) {
        days = Math.floor(time / 86400);
        hours = Math.floor((time % 86400) / 3600);
        minutes = Math.floor((time % 3600) / 60);
      }

      if (days > 0) {
        return `${days} ${this.$tc('pricingrules.day', days)} ${hours} ${this.$t('pricingrules.hour')} ${minutes} ${this.$t('pricingrules.minute')}`;
      } else if (hours > 0 && days === 0) {
        return `${hours} ${this.$t('pricingrules.hour')} ${minutes} ${this.$t('pricingrules.minute')}`;
      } else if (hours === 0 && days === 0) {
        return `${minutes} ${this.$t('pricingrules.minute')}`;
      }
      return `${minutes} ${this.$t('pricingrules.minute')}`;
    },

    // checkTimeLimitEdit() {
    //   if (this.form.product.is_time_limit_edit) {
    //     setTimeout(() => {
    //       this.canEditGuest = false;
    //       this.canEditEndTime = false;
    //     }, 10000);
    //   }
    // },
    inputVehicleReg() {
      this.vehicleValid = false;
    },
    canEditGuest() {
      if (!this.isNew) {
        const endTime = this.form.endTime;
        const now = moment().format('YYYY-MM-DD HH:mm:ss');

        this.isFinishParking = moment(endTime).isBefore(moment(now));


        if (this.isFinishParking) {
          this.canGuestEdit = false;
          this.canEditEndTime = false;
        }
      }
    },
    canEdit() {
      // Special case for latvia (!)
      if (process.env.DEFAULT_LANGUAGE === 'lv') {
        return this.isNew;
      }

      return this.isNew || (moment().isBefore(moment(this.parking.startTime).add(this.maximumEditingMinutes, 'minutes')) && this.parking.product.is_time_limit_edit === true);
    },
    saveOrder(order) {
      this.$store.dispatch('products/product/saveOrder', { user: this.user, order: order, tenantId: this.selectedTenant })
        .then(() => {
        });
    },
    checkView() {
      this.$store.commit(`pricingrules/pricingrules/${mTypes.CLEAR_STATE}`);
      this.resetValidators();
      this.addValidators();
      if (this.isLandlord()) {
        this.fetchTenants();
      }
      this.fetchTenantProducts();
      if (this.$route.params.id) {
        this.fetchParking();
      } else {
        this.$store.commit(`parkings/parking/${mTypes.CLEAR_STATE}`);
        this.$store.commit(`tenants/product/${mTypes.CLEAR_STATE}`);
        this.form = {};
        this.landlord = this.user.data.selectedRole.id;
        this.productId = null;
        this.ruleHasSteps = false;
        this.selectedTenant = null;
      }
    },
    trimVehicleReg() {
      if (this.form.vehicleReg) {
        this.form.vehicleReg = sanitizeVehicleRegNumber(this.form.vehicleReg);
      }
    },
    stopInputAtDashAndSpace(event) {
      if (event.key === '-' || event.key === ' ') {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    handleVehicleRegRemove(vehicleReg) {
      this.vehicleRegList.splice(this.vehicleRegList.indexOf(vehicleReg), 1);
    },
    handleVehicleRegAdd(event) {
      event.preventDefault();
      event.stopPropagation();

      this.vehicleValid = false;

      // clear input value
      let inputValue = sanitizeVehicleRegNumber(this.vehicleReg);
      // split possible multiple values
      inputValue = inputValue.split('.').join(',').split(';').join(',')
        .split(',');
      if (inputValue.length > 0) {
        inputValue.forEach((vehicleReg) => {
          if (vehicleReg.length > 0 && this.vehicleRegList.indexOf(vehicleReg) < 0) {
            this.vehicleRegList.push(vehicleReg);
          }
        });
      }
      this.vehicleReg = '';
    },
    fetchAllData() {
      if (this.isSuperAdmin()) {
        this.fetchLandlords();
      }
    },
    fetchTenants() {
      if (this.user.data) {
        this.$store.dispatch('landlord/tenants/list', {
          role: this.user.data.selectedRole.role,
          id: this.landlord ? this.landlord : this.user.data.selectedRole.id
        });
      }
    },
    fetchLandlords() {
      if (this.user.data) {
        this.$store.dispatch('landlord/landlords/list', { user: this.user });
      }
    },
    fetchOccupancy(month) {
      const selectedMonth = month || this.month;
      if (this.user.data) {
        this.$store.dispatch('parkings/parking/fetchOccupancy', { month: selectedMonth, year: this.year, productID: this.product ? this.product.id : undefined });
      }
    },
    getLanguage() {
      return this.$i18n.locale;
    },
    getPricingRulesForProduct() {
      if (this.productId && typeof this.productId !== 'undefined') {
        this.$store.dispatch('pricingrules/pricingrules/fetch', { id: this.productId, user: this.user, limit: 100 }).then(() => {
          this.pricingrules.forEach((el) => {
            this.ruleHasSteps = el.steps.length > 0;
          });
        });
      }
    },
    fetchTenantProducts() {
      if (this.isLandlord() && this.selectedTenant !== null && typeof this.selectedTenant !== 'undefined') {
        this.fetchProducts(this.user.data.selectedRole.role, this.user.data.selectedRole.id, this.selectedTenant)
      } else if (this.isTenant() && this.user.data) {
        this.fetchProducts(this.user.data.selectedRole.role, this.user.data.selectedRole.id)
      } else {
        this.products.data = {};
      }
    },
    fetchProducts(role, roleId, selectedTenant = null) {
      this.$store.dispatch('landlord/products/fetchProducts', {
        id: selectedTenant,
        role: role,
        roleId: roleId
      }).then(() => {
        this.orderBySorting()
        this.preSelectProduct()
      })
    },
    preSelectProduct() {
      if (!this.productId && this.products.data && this.products.data.length > 0) {
        this.productId = this.products.data[0].product.id;
      }
    },
    orderBySorting() {
      if (this.products.data && this.products.data.length > 0) {
        this.products.data = this.products.data.sort((a, b) => a.sorting - b.sorting);
      }
    },
    fetchParking() {
      this.$store.dispatch('parkings/parking/fetch', { id: this.$route.params.id, user: this.user }).then(() => {
        this.productId = this.parking.product.id;
        this.landlord = this.parking.partner.landlord.id;
        this.selectedTenant = this.parking.partner.id;

        this.form = JSON.parse(JSON.stringify(this.parking));

        const format = 'YYYY-MM-DD HH:mm:ss';
        if (this.form.startTime) this.form.startTime = moment(this.form.startTime).format(format);
        if (this.form.endTime) this.form.endTime = moment(this.form.endTime).format(format);

        this.originalStartTime = this.parking.startTime;
      });
    },
    updateForm() {
      if (this.$route.params.id) {
        this.form = this.parking;
        if (this.products.items !== undefined) {
          this.products.items.forEach((el) => {
            if (el.name === this.parking.name) {
              this.form.productId = el.id;
              this.productId = el.id;
            }
          });
        }
      }
    },
    onSubmit() {
      if (this.vehicleRegList.length === 0 &&
        this.$route.path.split('/').pop() !== 'edit') {
        this.vehicleValid = true;
      }
      this.$validator.validateAll()
        .then((success) => {
          if (success &&
            (this.vehicleRegList.length !== 0 ||
              this.$route.path.split('/').pop() === 'edit')) {
            this.form.productId = this.productId;
            this.form.startTime = this.getStartTime();
            if (this.form.endTime) {
              this.form.endTime = this.getEndTime();
            }
            this.submitted = true;
            if (this.isLandlord()) {
              this.form.partnerId = this.selectedTenant;
            }
            if (this.vehicleRegList.length > 0) {
              this.form.vehicleRegs = this.vehicleRegList.join(',');
              this.$store.dispatch('parkings/parking/saveBatch',
                { parking: this.form, user: this.user })
                .then((response) => {
                  if (response &&
                    response.body &&
                    response.body.registeredVehicles &&
                    response.body.registeredVehicles.length > 0 &&
                    !this.form.ignoreVehiclesRegisteredError) {
                    this.confirmationModalBody = this.$tc('parking.vehicles_registered_in_zone', response.body.registeredVehicles.length, { vehicleRegs: response.body.registeredVehicles.join(', ') });
                    this.showConfirmationModal = true;
                  } else if (this.parkingError) {
                    this.warningHeader = this.$t('common.warning');
                    if (this.isJSON(this.parkingError) && JSON.parse(this.parkingError).limit) {
                      const PARKINGERROR = JSON.parse(this.parkingError);
                      this.warningBody = this.$t(`errors.${PARKINGERROR.code}`, { timeOfParking: this.timeToText(this.product.time_limit), timeLeft: this.timeToText(PARKINGERROR.limit) });
                    } else {
                      this.warningBody = this.$t(`end_user.errors.${this.parkingError}`);
                    }
                    this.showWarningModal = true;
                    this.submitted = false;
                  } else {
                    this.resetValidators();
                    this.$store.dispatch('landlord/parkings/fetch', { user: this.user, limit: this.parkingsLimit, keyword: this.parkingsKeywordFilter });
                    this.$router.push({ name: 'parking', params: { id: response.body[0].id } });
                  }
                  this.form.ignoreVehiclesRegisteredError = false;
                });
            } else {
              this.$store.dispatch('parkings/parking/save', { parking: this.form, user: this.user })
                .then((response) => {
                  if (response &&
                    response.body &&
                    response.body.registeredVehicles &&
                    response.body.registeredVehicles.length > 0 &&
                    !this.form.ignoreVehiclesRegisteredError) {
                    this.confirmationModalBody = this.$tc('parking.vehicles_registered_in_zone', response.body.registeredVehicles.length, { vehicleRegs: response.body.registeredVehicles.join(', ') });
                    this.showConfirmationModal = true;
                  } else if (this.parkingError) {
                    this.warningHeader = this.$t('common.warning');
                    this.warningBody = this.$t(`errors.${this.parkingError}`);
                    this.showWarningModal = true;
                    this.submitted = false;
                  } else {
                    this.resetValidators();
                    this.$store.dispatch('landlord/parkings/fetch', { user: this.user, limit: this.parkingsLimit, keyword: this.parkingsKeywordFilter });
                    this.$router.push({ name: 'parking', params: { id: this.parking.id } });
                  }
                  this.form.ignoreVehiclesRegisteredError = false;
                });
            }
          }
        })
        .catch(() => {
        });
    },
    addValidators() {
      this.$validator.extend('isValidStartDate', {
        getMessage: () => 'Please insert valid start date.',
        validate: () => {
          if (!this.form.startTime) {
            return true;
          }
          const allowPast = this.product && this.product.allow_past_start;
          if (allowPast) {
            return this.form.endTime
              ? moment(this.form.startTime).isBefore(moment(this.form.endTime))
              : true;
          }
          const notInThePast = moment().subtract(5, 'minute').isSameOrBefore(moment(this.form.startTime), 'minute');
          return this.form.endTime
            ? notInThePast && moment(this.form.startTime).isBefore(moment(this.form.endTime))
            : notInThePast;
        },
      });
      this.$validator.extend('isValidEndDate', {
        getMessage: () => 'Please insert valid end date.',
        validate: () => {
          if (!this.form.endTime) {
            return true;
          }
          const notInThePast = moment().isSameOrBefore(moment(this.form.endTime), 'minute');
          return this.form.startTime
            ? notInThePast && moment(this.form.startTime).isBefore(moment(this.form.endTime))
            : notInThePast;
        },
      });
      this.createLandlordValidator();
    },
    createLandlordValidator() {
      this.$validator.extend('validLandlord', {
        getMessage: () => 'Tenant must be selected',
        validate: () => this.isSuperAdmin() && (typeof this.landlord !== 'undefined' && this.landlord !== null),
      });
    },
    resetValidators() {
      this.$validator.remove('isValidStartDate');
      this.$validator.remove('isValidEndDate');
      this.$validator.clean();
    },
    isRequiredComment() {
      if (this.product && Object.keys(this.product).length && this.product !== undefined) {
        return this.product.is_required_comment;
      } else if (this.form && Object.keys(this.form).length && this.form.product !== undefined) {
        return this.form.product.is_required_comment;
      }
      return false;
    },
    hasNotStarted() {
      if (this.isNew) return true;

      return moment().isBefore(moment(this.originalStartTime));
    },
    hasNotEnded() {
      if (this.notEnded === null) {
        this.notEnded = !moment(this.parking.startTime).isBefore(moment())
          ? !this.parking.endTime || moment().isBefore(moment(this.parking.endTime))
          : true;
      }
      return this.notEnded;
    },
    canEditPack() {
      // Special case for latvia (!)
      return process.env.DEFAULT_LANGUAGE === 'lv' ? (this.isNew) : true;
    },
    toDoubleDigits(digits) {
      return digits < 10 ? '0'.concat(String(digits)) : String(digits);
    },
    closeWarningModal() {
      this.showWarningModal = false;
      this.$store.commit(`parkings/parking/${mTypes.DATA_ERROR}`, null);
    },
    getStartTime() {
      if (this.form.startTime) {
        return moment(this.form.startTime).format('YYYY-MM-DD HH:mm:ss');
      }
      return moment().format('YYYY-MM-DD HH:mm:ss');
    },
    getEndTime() {
      if (this.form.endTime) {
        return moment(this.form.endTime).format('YYYY-MM-DD HH:mm:ss');
      }
      return this.form.endTime;
    },
    formChanges() {
      this.$validator.validateAll()
        .then((success) => {
          if (success) {
            const form = {};
            form.productId = this.productId;
            form.startTime = this.getStartTime();
            if (this.form.endTime) {
              form.endTime = this.getEndTime();
              const secondsBetweenDates = moment(form.endTime).diff(moment(form.startTime));
              if (secondsBetweenDates >= this.checkingSecondsBetweenDates) {
                this.endTimeOptions.pickerVisible = false;
                this.daysBetweenDates = Math.round(moment.duration(secondsBetweenDates, 'ms').format('DD'));
                this.showMoreThanTwoWeeksModal = true;
              }
            }
            if (this.isLandlord()) {
              form.partnerId = this.selectedTenant;
            }
            if (form.startTime === this.lastPriceRequest.startTime
              && form.endTime === this.lastPriceRequest.endTime
              && form.productId === this.lastPriceRequest.productId) {
              return;
            }
            this.lastPriceRequest = { startTime: form.startTime, endTime: form.endTime, productId: form.productId };
            this.$store.dispatch('parkings/parking/get_price', { parking: form, user: this.user })
              .then((response) => {
                if (typeof response.body.price !== 'undefined') {
                  this.expectedPrice = response.body.price;
                } else {
                  this.expectedPrice = null;
                }
              }).catch(() => {
                this.expectedPrice = null;
              });
          }
        })
        .catch(() => {
          this.expectedPrice = null;
        });
    },
    handleStartDateChange(value) {
      if(!this.isNew) {
        const now = moment();
        const selected = moment(value);

        if (selected.isBefore(now)) {
          this.form.startTime = now.format('YYYY-MM-DD HH:mm:ss');
        }
      }
      this.checkedDatesBetweenEachOther();
    },
    checkedDatesBetweenEachOther() {
      if (this.form.endTime) {
        this.secondsBetweenDates = moment(this.form.endTime).diff(moment(this.form.startTime));
        this.endTimeOptions.pickerVisible = false;

        if (this.secondsBetweenDates >= this.checkingSecondsBetweenDates) {
          this.daysBetweenDates = Math.round(moment.duration(this.secondsBetweenDates, 'ms').format('DD'));
          this.showMoreThanTwoWeeksModal = true;
        }
      }
    },
    closeDatePicker() {
      this.form.endTime = null;
      this.showMoreThanTwoWeeksModal = false;
    },
    confirmedDatePicker() {
      this.showMoreThanTwoWeeksModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/blocks/details.scss';

.form {
  width: 100%;

  &__fieldset {
    width: 70%;
  }

  &__buttons {
    float: right;
  }

  #vehicleReg {
    font-family: 'Geogrotesque Md', sans-serif !important;
  }

  .vehicle-reg-input {
    text-align: left;

    /deep/ .el-input__inner {
      border: 0;
    }

    .el-tag {
      background-color: var(--brand-green);
      border: 1px solid var(--brand-green);
      color: white;

      margin: 2px;
      height: 40px;
      line-height: 40px;
      padding: 0 10px 0 15px;
      cursor: pointer;

      /deep/ .el-tag__close {
        color: white;
      }

      /deep/ .el-tag__close:hover {
        background-color: transparent;
      }

    }
  }

  /deep/ .el-select {
    .el-input--suffix {
      .el-input__inner {
        padding: 11px 15px;
        height: 40px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/parkings/AddParkingView.vue